import { useState } from 'react'
import styled from 'styled-components'
import { RiShieldKeyholeLine } from 'react-icons/ri'
import PinInput from './PinInput'
import useTranslation from '../../hooks/useTranslation'
import { usePortal } from '../../hooks/usePortal'
import { Background, Card, Title, Subtitle, IconWrapper, Button, ErrorMsg } from './styles'


const PinGroup = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

const PinLabel = styled.label`
    font-size: 0.875rem;
    font-weight: 500;
    text-align: left;
    color: ${({ theme }) => theme.font?.color || '#777'};
`

const EMPTY_PIN = ['', '', '', '']

const PortalSetPasscode = ({ onComplete, onPasscodeExists, portalToken }) => {
    const [createPin, setCreatePin] = useState(EMPTY_PIN)
    const [confirmPin, setConfirmPin] = useState(EMPTY_PIN)
    const [mismatch, setMismatch] = useState(false)
    const [serverError, setServerError] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const { t } = useTranslation()
    const { setupPasscode } = usePortal()

    const createCode = createPin.join('')
    const confirmCode = confirmPin.join('')
    const complete = createCode.length === 4 && confirmCode.length === 4

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!complete) return

        setMismatch(false)
        setServerError(null)

        if (createCode !== confirmCode) {
            setMismatch(true)
            setCreatePin(EMPTY_PIN)
            setConfirmPin(EMPTY_PIN)
            return
        }

        setSubmitting(true)
        const result = await setupPasscode(portalToken, createCode)
        setSubmitting(false)

        if (result?.error) {
            if (result.error.name === 'error.passcode.already.set') {
                onPasscodeExists()
            } else {
                setServerError(t('portal.setup.error.saving'))
                setCreatePin(EMPTY_PIN)
                setConfirmPin(EMPTY_PIN)
            }
            return
        }

        onComplete(createCode)
    }

    return (
        <Background>
            <Card>
                <IconWrapper><RiShieldKeyholeLine /></IconWrapper>
                <Title>{t('portal.setup.title')}</Title>
                <Subtitle>{t('portal.setup.subtitle')}</Subtitle>
                <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1.25rem', alignItems: 'center' }}>
                    <PinGroup>
                        <PinLabel>{t('portal.setup.create.label')}</PinLabel>
                        <PinInput
                            values={createPin}
                            onChange={setCreatePin}
                            type="text"
                            autoFocus
                        />
                    </PinGroup>
                    <PinGroup>
                        <PinLabel>{t('portal.setup.confirm.label')}</PinLabel>
                        <PinInput
                            values={confirmPin}
                            onChange={setConfirmPin}
                            type="text"
                        />
                        {mismatch && <ErrorMsg>{t('portal.setup.mismatch')}</ErrorMsg>}
                    </PinGroup>
                    {serverError && <ErrorMsg>{serverError}</ErrorMsg>}
                    <Button type="submit" disabled={!complete || submitting}>
                        {t('portal.setup.submit')}
                    </Button>
                </form>
            </Card>
        </Background>
    )
}

export default PortalSetPasscode
